import React from 'react';
import ReactDOM from 'react-dom';
import type { IntlShape } from 'react-intl';
import type { EditorView } from 'prosemirror-view';
import { Decoration, DecorationSet } from 'prosemirror-view';
import type { EditorState, Transaction } from 'prosemirror-state';
import type { Node } from 'prosemirror-model';
import debounce from 'lodash/debounce';

import type {
	NextEditorPlugin,
	OptionalPlugin,
} from '@atlaskit/editor-common/types/next-editor-plugin';
import { SafePlugin } from '@atlaskit/editor-common/safe-plugin';

import { expValEquals } from '@confluence/feature-experiments';
import type { AnalyticsPlugin } from '@atlaskit/editor-plugin-analytics/analyticsPluginType';
import type { EditorAnalyticsAPI } from '@atlaskit/editor-common/analytics/api';

import { PLUGIN_KEY, PLUGIN_NAME } from './headingLinksConstants.ts';
import { LinkCopyComponent } from './LinkCopyComponent.tsx';

export const createLinkDecoration = (
	position: number,
	node: Node,
	isRightAligned: boolean,
	intl: IntlShape,
	analytics: EditorAnalyticsAPI | undefined,
	getHeadingLinkPath: () => string | null,
): Decoration => {
	return Decoration.widget(
		position,
		() => {
			// eslint-disable-next-line check-react-ssr-usage/guard-browser-globals
			const elem = document.createElement('span');

			// Trying to avoid both React and ProseMirror changing the same elements,
			// hence the nested container
			// eslint-disable-next-line check-react-ssr-usage/guard-browser-globals
			const container = document.createElement('span');

			elem.appendChild(container);

			const renderWidget = () => {
				// eslint-disable-next-line react/no-deprecated
				ReactDOM.render(
					<LinkCopyComponent
						node={node}
						intl={intl}
						analytics={analytics}
						isRightAligned={isRightAligned}
						getHeadingLinkPath={getHeadingLinkPath}
					/>,
					container,
				);
			};

			if (expValEquals('platform_editor_ai_xstate_migration', 'isEnabled', true)) {
				// Defer the render out of ProseMirror's synchronous updateChildren pass.
				// ReactDOM.render's flushSync, when run mid-reconciliation, commits a
				// sibling table nodeview's remount synchronously and causes a visible
				// flash (seen during the xstate AI finalize which edits a heading above
				// a table). Deferring lets React batch the update normally.
				void Promise.resolve().then(renderWidget);
			} else {
				renderWidget();
			}

			return elem;
		},
		{ side: isRightAligned ? -1 : 1, key: `heading-link-${position}`, ignoreSelection: false },
	);
};

type PluginType = {
	hoveredHeading: Node | undefined;
	start: number | undefined;
};
export const createPlugin = (
	intl: IntlShape,
	analytics: EditorAnalyticsAPI | undefined,
	getHeadingLinkPath: () => string | null,
): SafePlugin<PluginType> => {
	const handleMouseOver = (view: EditorView, event: any) => {
		// Get the position of the cursor
		const result = view.posAtCoords({
			left: event.clientX,
			top: event.clientY,
		});
		const pos = result?.pos;

		if (pos !== undefined) {
			// Resolve the position to a node
			let range = view.state.doc.resolve(pos);
			if (range.parent.type.name !== 'heading') {
				// There was an issue where, if you hovered over a heading from top to bottom,
				// the position would resolve to the document node rather than the heading.
				// Offsetting by 1 seems to fix the issue. However, we need to check both
				// pos and pos+1. If we only check the latter, there's a similar problem when
				// hovering headings from right to left.
				const adjPos = Math.min(pos + 1, view.state.doc.content.size);
				range = view.state.doc.resolve(adjPos);
			}

			// If the node is a heading, dispatch metadata to add a link decoration
			if (range.parent.type.name === 'heading') {
				view.dispatch(
					view.state.tr.setMeta(PLUGIN_KEY, {
						applyHoverDecoration: true,
						from: range.start(),
						node: range.parent,
					}),
				);
			}
		}

		return false;
	};
	const debouncedHandleMouseOver = debounce(handleMouseOver, 150);

	const plugin: SafePlugin<PluginType> = new SafePlugin<PluginType>({
		key: PLUGIN_KEY,
		state: {
			init() {
				return { hoveredHeading: undefined, start: undefined };
			},
			apply: (tr: Transaction, old: PluginType, _: EditorState, newState: EditorState) => {
				// Get the metadata that was dispatched from the mouseover event
				const hoverDecoration = tr.getMeta(PLUGIN_KEY);

				// If the metadata `applyHoverDecoration` property is present,
				// add a link decoration to the heading node. Note: Only one
				// decoration will ever exist on the page. When a new heading
				// is hovered, the old decoration will be replaced.
				if (hoverDecoration?.applyHoverDecoration) {
					const node = hoverDecoration.node as Node;
					return { hoveredHeading: node, start: hoverDecoration.from };
				}

				if (old.start !== undefined) {
					const newStart = tr.mapping.map(old.start);
					const newHeadingNode = newState.doc.resolve(newStart);

					if (
						newHeadingNode.parent.content.size === 0 ||
						newHeadingNode.parent.type.name !== 'heading'
					) {
						return { hoveredHeading: undefined, start: undefined };
					}

					// If the heading is prepended, the start position will be off by 1,
					// so we need to use the .start() method to get the correct position.
					return { hoveredHeading: newHeadingNode.parent, start: newHeadingNode.start() };
				}

				return old;
			},
		},
		props: {
			decorations(state: EditorState) {
				const pluginState = plugin.getState(state);
				const heading = pluginState?.hoveredHeading;
				const start = pluginState?.start;

				if (heading && start !== undefined) {
					const endMark = heading.marks.find((mark) => mark.attrs?.align === 'end');
					const position = endMark ? start : start + heading.content.size;
					const link = createLinkDecoration(
						position,
						heading,
						endMark !== undefined,
						intl,
						analytics,
						getHeadingLinkPath,
					);

					return DecorationSet.create(state.doc, [link]);
				}

				return DecorationSet.empty;
			},
			handleDOMEvents: {
				mouseover: debouncedHandleMouseOver,
			},
		},
	});

	return plugin;
};

type HeadingLinksPlugin = NextEditorPlugin<
	'heading-links',
	{
		dependencies: [OptionalPlugin<AnalyticsPlugin>];
		pluginConfiguration: {
			getHeadingLinkPath: () => string | null;
		};
	}
>;
export const EditorHeadingLinksPlugin: HeadingLinksPlugin = ({
	api,
	config: { getHeadingLinkPath },
}) => ({
	name: PLUGIN_NAME,
	pmPlugins() {
		return [
			{
				name: PLUGIN_NAME,
				plugin: ({ getIntl }) => {
					return createPlugin(getIntl(), api?.analytics?.actions, getHeadingLinkPath);
				},
			},
		];
	},
});
